import type { Command, EditorState } from 'prosemirror-state';
import type { IntlShape } from 'react-intl-next';

import type {
	ExtractInjectionAPI,
	FloatingToolbarConfig,
	FloatingToolbarItem,
} from '@atlaskit/editor-common/types';
import { isEmptyNode as isEmptyNodeWithSchema } from '@atlaskit/editor-common/utils';
import type { NodeType, Schema } from '@atlaskit/editor-prosemirror/model';
import RandomizeIcon from '@atlaskit/icon-lab/core/randomize';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { messages } from '../messages';

export function getUnallowedNodes(schema: Schema): Set<NodeType> {
	// https://hello.atlassian.net/wiki/spaces/ic4/pages/6363616999/Malleable+Content+One+Pager#remix-block-allowed-nodes
	return new Set(
		[
			// 1P embeds (ideally we will have this by Team) + 3P embeds
			schema.nodes.embedCard,
			schema.nodes.blockCard,
			// Media (images)
			schema.nodes.mediaSingle,
			schema.nodes.mediaGroup,
			schema.nodes.media,
			schema.nodes.image,
			// 1P & 3P Macros (extensions)
			schema.nodes.extension, // This will exclude "An existing Remix" and also all Native Embeds
			schema.nodes.bodiedExtension,
			schema.nodes.multiBodiedExtension,
			// Synced Blocks
			schema.nodes.bodiedSyncBlock,
		].filter(Boolean),
	);
}

export function getOverriddenFloatingToolbarConfig(api?: ExtractInjectionAPI<MalleableUiPlugin>) {
	if (!api?.toolbar) {
		return () => undefined;
	}

	return (
		state: EditorState,
		intl: IntlShape,
		_providerFactory: unknown,
		activeConfigs?: Array<FloatingToolbarConfig>,
	): FloatingToolbarConfig | undefined => {
		if (!activeConfigs?.length) {
			return;
		}

		const unallowedNodes = getUnallowedNodes(state.schema);
		const isNodeEmpty = isEmptyNodeWithSchema(state.schema);
		activeConfigs.forEach((config) => {
			if (
				([] as NodeType[])
					.concat(config.nodeType)
					.some((nodeType) => unallowedNodes.has(nodeType) || nodeType.isInline)
			) {
				return;
			}

			const toolbarItems = config.items;
			if (Array.isArray(toolbarItems)) {
				const remixButton: FloatingToolbarItem<Command> = {
					id: 'editor.malleable-ui.remix',
					type: 'button',
					title: intl.formatMessage(messages.remixLabel),
					tooltipContent: intl.formatMessage(messages.remixTooltip),
					showTitle: true,
					hideTooltipOnClick: false,
					icon: RandomizeIcon,
					onClick: () => false,
					disabled: false,
				};
				const remixToolbarItems: FloatingToolbarItem<Command>[] = [
					remixButton,
					{
						type: 'separator',
						fullHeight: true,
					},
				];

				toolbarItems.splice(0, 0, ...remixToolbarItems);
				config.items = (node) => {
					remixButton.onClick = (editorState) => {
						const $pos = editorState.selection.$from;
						let nodePos: number | undefined;
						for (let depth = $pos.depth; depth > 0; depth--) {
							if ($pos.node(depth) === node) {
								nodePos = $pos.before(depth);
								break;
							}
						}
						api.core.actions.execute(api.malleableUi.commands.openRemixModal(nodePos));
						return true;
					};
					remixButton.disabled = isNodeEmpty(node);
					return toolbarItems;
				};
			}
		});
	};
}
